import { useState, useEffect } from "react";
import { MdChevronRight } from "react-icons/md";
import AtriumApi from "api";
import Button from "components/button";
import { Checkbox, DatePicker, InputField, Textarea } from "components/fields";
import { SegmentedControl } from "components/tabs";
import MapboxAddressInput from "components/mapbox";
import TimePicker from "components/time-picker/TimePicker";
import FileUpload from "components/file-upload/FileUpload";

const SERVICE_TYPES = ["Worship", "Prayer", "Outreach", "Youth", "Kids", "Other"];
const PRESET_TYPES = SERVICE_TYPES.slice(0, -1);

const SCHEDULE_OPTIONS = [
  { value: "once", label: "Once" },
  { value: "weekly", label: "Weekly" },
];

const FieldError = ({ children }) =>
  children ? <p className="mt-1 text-xs text-danger-fg">{children}</p> : null;

const Fieldset = ({ legend, hint, error, children }) => (
  <fieldset className="flex flex-col gap-2">
    <legend className="mb-1.5 text-sm font-medium text-fg">{legend}</legend>
    {hint && <p className="-mt-1 text-xs text-fg-muted">{hint}</p>}
    {children}
    <FieldError>{error}</FieldError>
  </fieldset>
);

/**
 * Service details form used in the create/edit drawer.
 */
const ModernServiceForm = ({ service, setService, errors = {}, setErrors }) => {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [campuses, setCampuses] = useState([]);
  const [showCustomAddress, setShowCustomAddress] = useState(false);

  // Fetch campuses on mount
  useEffect(() => {
    const fetchCampuses = async () => {
      try {
        const response = await AtriumApi.get("/v2/campuses/allCampuses");
        const campusData = response?.data?.data?.campuses?.data || [];
        setCampuses(campusData);

        // Default to all campuses if creating new service (no existing ID)
        if (!service?.id && !service?.campusIds?.length && campusData.length > 0) {
          setService((prev) => ({
            ...prev,
            campusIds: campusData.map((c) => c.id),
          }));
        }
      } catch (error) {
        console.error("Failed to load campuses:", error);
      }
    };
    fetchCampuses();
  }, []);

  const handleFieldChange = (field, value) => {
    setService({ ...service, [field]: value });
    if (errors[field] && setErrors) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const toggleCampus = (campusId) => {
    const currentIds = service?.campusIds || [];
    const newIds = currentIds.includes(campusId)
      ? currentIds.filter((id) => id !== campusId)
      : [...currentIds, campusId];
    handleFieldChange("campusIds", newIds);
  };

  // Format helpers - parse date string directly to avoid timezone issues
  const formatToMMDDYYYY = (date) => {
    if (!date) return "";

    if (date instanceof Date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${month}/${day}/${year}`;
    }

    const dateStr = String(date);

    // Already MM/DD/YYYY
    if (dateStr.includes("/")) return dateStr;

    // Parse YYYY-MM-DD directly without timezone conversion
    const [year, month, day] = dateStr.split("-");
    return `${month}/${day}/${year}`;
  };

  const formatTo12HourFromString = (timeStr) => {
    if (!timeStr) return "";
    const [hourStr, minuteStr] = timeStr.split(":");
    const hour = parseInt(hourStr, 10);
    const minute = parseInt(minuteStr, 10);
    const h = hour % 12 || 12;
    const m = String(minute).padStart(2, "0");
    const period = hour < 12 ? "AM" : "PM";
    return `${String(h).padStart(2, "0")}:${m} ${period}`;
  };

  const formatTo24Hour = (timeStr) => {
    if (!timeStr) return "";
    const [time, modifier] = timeStr.split(" ");
    let [hours, minutes] = time.split(":").map(Number);

    if (modifier === "PM" && hours !== 12) hours += 12;
    if (modifier === "AM" && hours === 12) hours = 0;

    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:00`;
  };

  // Auto-correct the end time's AM/PM only when that yields a sensible same-day window
  const handleEndTimeChange = (timeStr) => {
    if (!timeStr) {
      handleFieldChange("endAt", "");
      return;
    }

    if (!service?.startAt) {
      handleFieldChange("endAt", formatTo24Hour(timeStr));
      return;
    }

    const [time, modifier] = timeStr.split(" ");
    const [hours] = time.split(":").map(Number);

    let endHour24 = hours;
    if (modifier === "PM" && hours !== 12) endHour24 = hours + 12;
    if (modifier === "AM" && hours === 12) endHour24 = 0;

    const startHour = parseInt(service.startAt.split(":")[0]);

    // Flip AM/PM only if the end is not after the start, the flipped time is
    // valid and the resulting duration is 12 hours or less.
    if (endHour24 <= startHour) {
      const flippedHour = modifier === "AM" ? endHour24 + 12 : endHour24 - 12;
      const wouldBeValid = flippedHour > startHour && flippedHour >= 0 && flippedHour < 24;
      const reasonableDuration = wouldBeValid && flippedHour - startHour <= 12;

      if (wouldBeValid && reasonableDuration) {
        const newModifier = modifier === "AM" ? "PM" : "AM";
        handleFieldChange("endAt", formatTo24Hour(`${time} ${newModifier}`));
        return;
      }
    }

    handleFieldChange("endAt", formatTo24Hour(timeStr));
  };

  const selectLocationCampus = (campus) => {
    const campusAddress = [campus.address, campus.city, campus.state, campus.zipCode]
      .filter(Boolean)
      .join(", ");
    setShowCustomAddress(false);
    setService((prev) => ({
      ...prev,
      locationCampusId: campus.id,
      address: campusAddress || campus.name,
      locationName: campus.name,
      location: campus.location || null,
    }));
  };

  const selectCustomAddress = () => {
    setShowCustomAddress(true);
    setService((prev) => ({
      ...prev,
      locationCampusId: null,
      address: "",
      locationName: "",
      location: null,
    }));
  };

  const addressInput = (
    <MapboxAddressInput
      placeholder="Search by address or location name"
      value={service?.address}
      onResult={(data) => {
        setService((prev) => ({
          ...prev,
          ...(campuses.length > 0 ? { locationCampusId: null } : {}),
          address: data?.address || "",
          locationName: data?.locationName || "",
          location: {
            type: "Point",
            coordinates: [data?.lng, data?.lat],
          },
        }));
      }}
    />
  );

  const hasCustomType = service?.type && !PRESET_TYPES.includes(service.type);

  return (
    <div className="flex flex-col gap-5">
      <InputField
        label="Service Name"
        required
        placeholder="Add service name"
        value={service?.name || ""}
        onChange={(e) => handleFieldChange("name", e.target.value)}
        error={errors.name}
      />

      <div className="flex flex-col gap-3 rounded-control bg-surface-raised p-4">
        <DatePicker
          label="Date"
          placeholder="Select date"
          error={errors.date}
          value={formatToMMDDYYYY(service?.date)}
          onChange={(v) => handleFieldChange("date", formatToMMDDYYYY(v))}
          isPastDateDisabled
        />

        <div className="grid grid-cols-2 gap-3">
          <TimePicker
            label="Start"
            placeholder="Start time"
            value={formatTo12HourFromString(service?.startAt)}
            onChange={(e) => handleFieldChange("startAt", formatTo24Hour(e.target.value))}
            error={errors.startAt}
          />
          <TimePicker
            label="End"
            placeholder="End time"
            value={formatTo12HourFromString(service?.endAt)}
            onChange={(e) => handleEndTimeChange(e.target.value)}
            error={errors.endAt}
          />
        </div>
      </div>

      <Fieldset legend="Service Type" error={errors.type}>
        <div className="grid grid-cols-3 gap-2">
          {SERVICE_TYPES.map((type) => {
            const pressed = service?.type === type;
            return (
              <Button
                key={type}
                variant={pressed ? "primary" : "secondary"}
                aria-pressed={pressed}
                onClick={() => handleFieldChange("type", pressed ? "" : type)}
              >
                {type}
              </Button>
            );
          })}
        </div>
        {hasCustomType && (
          <InputField
            aria-label="Custom service type"
            placeholder="Custom service type"
            value={service?.type || ""}
            onChange={(e) => handleFieldChange("type", e.target.value)}
          />
        )}
      </Fieldset>

      {campuses.length > 0 && (
        <Fieldset
          legend="Campuses"
          hint="Select which campuses offer this service. Members will see this service when filtering by their campus."
          error={errors.campusIds}
        >
          <div className="flex flex-wrap gap-x-5 gap-y-2">
            {campuses.map((campus) => (
              <Checkbox
                key={campus.id}
                label={campus.isPrimary ? `${campus.name} (Primary)` : campus.name}
                checked={(service?.campusIds || []).includes(campus.id)}
                onChange={() => toggleCampus(campus.id)}
              />
            ))}
          </div>
        </Fieldset>
      )}

      <Fieldset legend="Repeat" error={errors.schedule}>
        <SegmentedControl
          aria-label="Repeat"
          options={SCHEDULE_OPTIONS}
          value={service?.schedule || ""}
          onChange={(value) => handleFieldChange("schedule", value)}
          className="self-start"
        />
      </Fieldset>

      <Fieldset legend="Location" hint="The physical address where this service will be held.">
        {campuses.length > 0 ? (
          <>
            <div className="flex flex-wrap gap-2">
              {campuses.map((campus) => {
                const pressed = service?.locationCampusId === campus.id && !showCustomAddress;
                return (
                  <Button
                    key={campus.id}
                    size="sm"
                    variant={pressed ? "primary" : "secondary"}
                    aria-pressed={pressed}
                    onClick={() => selectLocationCampus(campus)}
                  >
                    {campus.name}
                  </Button>
                );
              })}
              <Button
                size="sm"
                variant={showCustomAddress ? "primary" : "secondary"}
                aria-pressed={showCustomAddress}
                onClick={selectCustomAddress}
              >
                Other Address
              </Button>
            </div>
            {showCustomAddress && addressInput}
            {service?.locationCampusId && service?.address && !showCustomAddress && (
              <p className="text-sm text-fg-muted">{service.address}</p>
            )}
          </>
        ) : (
          addressInput
        )}
      </Fieldset>

      <Textarea
        label="Description"
        placeholder="Add notes or details (optional)"
        value={service?.description || ""}
        onChange={(e) => handleFieldChange("description", e.target.value)}
        rows={3}
        error={errors.description}
      />

      <div className="border-t border-line pt-4">
        <Button
          variant="ghost"
          size="sm"
          className="-ml-3"
          aria-expanded={showAdvanced}
          leftIcon={
            <MdChevronRight
              aria-hidden="true"
              className={`h-4 w-4 transition-transform ${showAdvanced ? "rotate-90" : ""}`}
            />
          }
          onClick={() => setShowAdvanced(!showAdvanced)}
        >
          {showAdvanced ? "Hide" : "Show"} Advanced Options
        </Button>

        {showAdvanced && (
          <div className="mt-4">
            <Fieldset legend="Attachments">
              <FileUpload
                files={service?.fileObjects || []}
                onFilesChange={(files) => handleFieldChange("fileObjects", files)}
              />
            </Fieldset>
          </div>
        )}
      </div>
    </div>
  );
};

export default ModernServiceForm;
